<template>
  <div class="rounded-lg border bg-card p-6">
    <h3 class="text-base font-semibold text-foreground">{{ $ts('checkout.summary.title') }}</h3>

    <!-- Partner & service info -->
    <div class="mt-4 flex items-start gap-3">
      <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Icon name="tabler:building-store" size="24" class="text-muted-foreground" />
      </div>
      <div class="min-w-0">
        <p class="font-medium text-foreground">{{ partnerName }}</p>
        <p class="text-sm text-primary">{{ serviceName }}</p>
      </div>
    </div>

    <Separator class="my-4" />

    <!-- Date & Time -->
    <div class="space-y-3">
      <div>
        <p class="text-xs font-medium text-primary">{{ $ts('checkout.summary.dateTime') }}</p>
        <div class="mt-1 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Icon name="tabler:calendar" size="16" class="text-primary" />
            <div>
              <p class="text-sm font-medium text-foreground">{{ formattedDate }}</p>
              <p class="text-sm text-muted-foreground">{{ time }}</p>
            </div>
          </div>
          <button
            class="text-sm font-medium text-primary hover:underline"
            @click="emit('editDateTime')"
          >
            {{ $ts('checkout.summary.edit') }}
          </button>
        </div>
      </div>

      <Separator />

      <!-- Duration -->
      <div class="flex items-center justify-between">
        <span class="text-sm text-muted-foreground">{{ $ts('checkout.summary.duration') }}</span>
        <span class="text-sm font-medium text-foreground">{{ duration }} {{ $ts('checkout.summary.hours') }}</span>
      </div>

      <!-- Specialist -->
      <div class="flex items-center justify-between">
        <span class="text-sm text-muted-foreground">{{ $ts('checkout.summary.specialist') }}</span>
        <span class="text-sm font-medium text-foreground">{{ specialistName }}</span>
      </div>

      <Separator />

      <!-- Total -->
      <div class="flex items-center justify-between">
        <span class="text-base font-medium text-muted-foreground">{{ $ts('checkout.summary.total') }}</span>
        <span class="text-xl font-bold text-foreground">{{ formattedPrice }}</span>
      </div>
    </div>

    <!-- Pay & confirm button -->
    <Button
      class="mt-5 w-full"
      size="lg"
      @click="emit('confirm')"
    >
      {{ $ts('checkout.summary.payAndConfirm') }}
    </Button>

    <!-- Terms -->
    <p class="mt-3 text-center text-xs text-muted-foreground">
      <i18n-t keypath="checkout.summary.termsAgreement">
        <template #terms>
          <NuxtLink :to="$localePath('/legal')" class="text-primary hover:underline">
            {{ $ts('checkout.summary.termsLink') }}
          </NuxtLink>
        </template>
      </i18n-t>
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '#imports';
import Separator from '#bubba/shared/components/ui/separator/Separator.vue';
import Button from '#bubba/shared/components/ui/button/Button.vue';
import { formatCurrency } from '#bubba/shared/utils/currency';

const { $ts, $localePath, $getLocale } = useI18n();

const props = defineProps<{
  partnerName: string;
  serviceName: string;
  date: Date;
  time: string;
  duration: number;
  specialistName: string;
  price: number;
  currency: string;
}>();

const emit = defineEmits<{
  confirm: [];
  editDateTime: [];
}>();

const formattedDate = computed(() => {
  return props.date.toLocaleDateString($getLocale(), {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
});

const formattedPrice = computed(() => {
  return formatCurrency(props.price, $getLocale(), props.currency, 2);
});
</script>
